import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../context/ProductStore';

const BOT_ICON = "https://img.icons8.com/3d-fluency/94/technical-support.png";

// ── Quick question buttons ────────────────────────────────────
const QUICK_QUESTIONS = [
  "What products do you sell?",
  "Do you repair laptops?",
  "What are your opening hours?",
  "Where are you located?",
  "What payment methods do you accept?",
  "Do you have laptops in stock?",
];

// ── Smart response engine ─────────────────────────────────────
const generateReply = (input, products) => {
  const q = input.toLowerCase().trim();

  const inStock     = products.filter(p => !p.outOfStock);
  const outOfStock  = products.filter(p => p.outOfStock);
  const promos      = products.filter(p => p.isPromo && !p.outOfStock);

  if (/^(hi|hello|hey|good morning|good afternoon|good evening|howzit|mhoro|sawubona)/.test(q)) {
    return `👋 Hello! Welcome to Techdave Solutions. I'm TechBot and I'm here to help you find the right tech product or answer any questions you have. What are you looking for today?`;
  }

  if (/thank|thanks|appreciate|great|awesome|perfect/.test(q)) {
    return `😊 You're welcome! Is there anything else I can help you with? Feel free to ask about any of our products or services.`;
  }

  const priceMatch = q.match(/(?:how much|price|cost|how much is|what is the price of|pricing)\s+(?:is|of|for)?\s*(.+)/);
  const searchTerm = priceMatch ? priceMatch[1].trim() : q.replace(/\?/g, '').trim();

  const matchedProducts = inStock.filter(p =>
    p.name.toLowerCase().includes(searchTerm) ||
    p.category.toLowerCase().includes(searchTerm) ||
    searchTerm.split(' ').some(word => word.length > 2 && p.name.toLowerCase().includes(word))
  );

  if ((priceMatch || /price|cost|how much/.test(q)) && matchedProducts.length > 0) {
    if (matchedProducts.length === 1) {
      const p = matchedProducts[0];
      if (p.isPromo) {
        return `🔥 *${p.name}*\n💰 Promo Price: USD ${p.promoPrice} (was USD ${p.price})\n📦 In Stock ✅\n${p.description ? `\n${p.description}` : ''}\n\nWould you like to add this to your cart?`;
      }
      return `📦 *${p.name}*\n💰 Price: USD ${p.price}\n✅ In Stock\n${p.description ? `\n${p.description}` : ''}\n\nWould you like to add this to your cart?`;
    }
    const list = matchedProducts.slice(0, 5).map(p => {
      const price = p.isPromo ? `USD ${p.promoPrice} 🔥` : `USD ${p.price}`;
      return `• ${p.name} — ${price}`;
    }).join('\n');
    return `Here are the matching products I found:\n\n${list}\n\nWould you like more details on any of these?`;
  }

  const categoryKeywords = {
    'Laptops':      ['laptop', 'laptops', 'notebook', 'hp laptop', 'dell laptop', 'lenovo', 'acer'],
    'Phones':       ['phone', 'phones', 'smartphone', 'mobile', 'samsung', 'iphone', 'tecno', 'infinix'],
    'Desktops':     ['desktop', 'desktops', 'pc', 'computer', 'tower'],
    'Printers':     ['printer', 'printers', 'printing', 'laserjet', 'inkjet'],
    'PA Systems':   ['pa system', 'pa systems', 'speaker', 'speakers', 'sound system', 'amplifier', 'public address'],
    'Networking':   ['router', 'routers', 'networking', 'network', 'wifi', 'switch', 'cable', 'internet'],
    'Accessories':  ['accessories', 'keyboard', 'mouse', 'headset', 'headphones', 'usb', 'cable', 'adapter', 'charger'],
    'Laptop Bags':  ['bag', 'bags', 'laptop bag', 'backpack', 'carry'],
  };

  for (const [category, keywords] of Object.entries(categoryKeywords)) {
    if (keywords.some(k => q.includes(k))) {
      const catProducts = inStock.filter(p => p.category === category);
      if (catProducts.length === 0) {
        return `We currently don't have any ${category} listed in stock right now, but we do stock them regularly. Visit us at B30 Eastgate Mall or check back soon — our catalogue updates frequently! 🏪`;
      }
      const list = catProducts.slice(0, 6).map(p => {
        const price = p.isPromo ? `USD ${p.promoPrice} 🔥 (was USD ${p.price})` : `USD ${p.price}`;
        return `• ${p.name} — ${price}`;
      }).join('\n');
      const more = catProducts.length > 6 ? `\n\n...and ${catProducts.length - 6} more in store!` : '';
      return `Here are our available ${category}:\n\n${list}${more}\n\nWould you like details on any specific product?`;
    }
  }

  if (matchedProducts.length > 0 && !priceMatch) {
    if (matchedProducts.length === 1) {
      const p = matchedProducts[0];
      const price = p.isPromo ? `USD ${p.promoPrice} 🔥 (was USD ${p.price})` : `USD ${p.price}`;
      return `📦 *${p.name}*\n💰 Price: ${price}\n✅ In Stock\n${p.description ? `\nℹ️ ${p.description}` : ''}\n\nShall I help you with anything else?`;
    }
    const list = matchedProducts.slice(0, 5).map(p => {
      const price = p.isPromo ? `USD ${p.promoPrice} 🔥` : `USD ${p.price}`;
      return `• ${p.name} (${p.category}) — ${price}`;
    }).join('\n');
    return `I found these matching products:\n\n${list}\n\nWant more details on any of these?`;
  }

  if (/what.*sell|what.*stock|what.*available|what.*have|products|catalogue|catalog|inventory/.test(q)) {
    const categories = [...new Set(inStock.map(p => p.category))];
    if (inStock.length === 0) {
      return `We sell Laptops, Phones, Desktops, Printers, PA Systems, Networking Equipment, Accessories and Laptop Bags. Visit us at B30 Eastgate Mall, Harare to see our full range! 🏪`;
    }
    const catSummary = categories.map(cat => {
      const count = inStock.filter(p => p.category === cat).length;
      return `• ${cat} (${count} available)`;
    }).join('\n');
    return `We currently have ${inStock.length} products in stock across these categories:\n\n${catSummary}\n\nAsk me about any category for specific products and prices! 😊`;
  }

  if (/promo|promotion|deal|discount|sale|offer|special/.test(q)) {
    if (promos.length === 0) {
      return `We don't have active promotions listed right now, but we always have competitive prices. Ask me about any specific product and I'll give you the best price! 💰`;
    }
    const list = promos.slice(0, 5).map(p =>
      `• ${p.name} — USD ${p.promoPrice} 🔥 (was USD ${p.price}, save USD ${(p.price - p.promoPrice).toFixed(2)})`
    ).join('\n');
    return `🔥 Here are our current promotion deals:\n\n${list}\n\nThese are special prices for a limited time. Want to order any of these?`;
  }

  if (/out of stock|not available|unavailable/.test(q)) {
    if (outOfStock.length === 0) {
      return `Great news — all our listed products are currently in stock! Ask me about any product and I'll check availability for you. ✅`;
    }
    return `We currently have ${outOfStock.length} product(s) temporarily out of stock. However, we restock regularly. Let me know what you're looking for and I'll tell you if we have it available. 📦`;
  }

  if (/repair|fix|broken|not working|screen|battery|service|servicing/.test(q)) {
    return `🔧 Yes, we offer professional repair and maintenance services including:\n\n• Laptop screen replacement\n• Battery replacement\n• Software troubleshooting & reinstallation\n• Hardware repairs\n• General computer servicing\n\nBring your device to us at B30 Eastgate Mall, Harare. Monday to Saturday, 8AM–6PM.`;
  }

  if (/network setup|wiring|install|setup|cctv|configure/.test(q)) {
    return `🌐 We provide full networking solutions including:\n\n• Home & office WiFi setup\n• Network cabling & wiring\n• Router & switch configuration\n• Internet sharing setup\n\nVisit us at B30 Eastgate Mall or describe your setup needs and I can give you more details!`;
  }

  if (/event|church|school|hire|rent|pa|sound|audio/.test(q)) {
    return `🔊 We stock professional PA systems suitable for:\n\n• Churches\n• Schools\n• Events & conferences\n• Businesses\n\nWe also assist with installation and setup. Visit us at B30 Eastgate Mall to see what's available or ask me for specific PA system products!`;
  }

  if (/bulk|corporate|company|business|order|quote|quotation|invoice/.test(q)) {
    return `🏢 We provide corporate supply services including:\n\n• Bulk orders for businesses & schools\n• Competitive pricing on large orders\n• Official invoicing & receipts\n• After-sales support\n\nFor a quotation, visit us at B30 Eastgate Mall, Harare or call us on +263 772 758 875 during business hours.`;
  }

  if (/where|location|address|find you|directions|eastgate|harare/.test(q)) {
    return `📍 You can find us at:\n\n*B30 Eastgate Mall*\nHarare, Zimbabwe\n\n🕐 Opening Hours:\nMonday – Saturday: 8:00 AM – 6:00 PM\n\nWe're easy to find — just head to Eastgate Mall and look for Shop B30!`;
  }

  if (/hours|open|close|time|when|monday|saturday|sunday/.test(q)) {
    return `🕐 Our opening hours are:\n\nMonday – Saturday: 8:00 AM – 6:00 PM\n\nWe are closed on Sundays and public holidays. Visit us at B30 Eastgate Mall, Harare. We'd love to see you! 😊`;
  }

  if (/pay|payment|ecocash|swipe|card|transfer|zipit|cash/.test(q)) {
    return `💳 We accept the following payment methods:\n\n📱 *EcoCash* — Send to 0772 758 875 or 0785 661 758\n💳 *Swipe / Card* — Pay by debit or credit card in store\n🏦 *Bank Transfer / ZIPIT* — Transfer to our CBZ account\n\nAll payments are safe and confirmed before your order is processed. ✅`;
  }

  if (/deliver|delivery|shipping|send|courier|bring/.test(q)) {
    return `🚚 We offer delivery within Harare for most products. Delivery fees vary depending on your location.\n\nYou can also collect your order for free at our store:\n📍 B30 Eastgate Mall, Harare\n🕐 Mon–Sat: 8AM–6PM\n\nFor delivery enquiries on a specific order, let us know your location and we'll sort it out!`;
  }

  if (/contact|call|number|phone number|reach|get in touch|email/.test(q)) {
    return `📞 You can reach us on:\n\n• +263 772 758 875\n• +263 785 661 758\n\n✉️ sales@techdavesolutions.co.zw\n\n🕐 Available Monday–Saturday, 8AM–6PM\n📍 Or visit us at B30 Eastgate Mall, Harare\n\nWe're always happy to help! 😊`;
  }

  if (/warrant|guarantee|return|refund|exchange/.test(q)) {
    return `✅ All our products come with a warranty. The duration depends on the brand and product type:\n\n• Laptops & Desktops: Typically 1 year\n• Phones: Manufacturer warranty applies\n• Accessories: 3–6 months\n\nFor specific warranty info on a product, visit us at B30 Eastgate Mall and our team will advise you fully.`;
  }

  if (/order|buy|purchase|add to cart|checkout/.test(q)) {
    return `🛒 You can order directly on our website:\n\n1️⃣ Browse products on the Shop page\n2️⃣ Click "Add to Cart" on items you want\n3️⃣ Click the Cart button and proceed to Checkout\n4️⃣ Enter your details and choose payment\n5️⃣ Place your order — our team will confirm via call or WhatsApp!\n\nEasy and fast. Want me to tell you about any specific product?`;
  }

  if (/how many|stock count|total products|how many products/.test(q)) {
    return `📦 We currently have *${inStock.length} products* available in stock across all categories.\n\nAsk me about a specific category or product and I'll give you exact details and prices! 😊`;
  }

  if (inStock.length > 0) {
    const words = q.split(' ').filter(w => w.length > 2);
    const broadMatch = inStock.filter(p =>
      words.some(w => p.name.toLowerCase().includes(w) || p.category.toLowerCase().includes(w))
    );
    if (broadMatch.length > 0) {
      const list = broadMatch.slice(0, 4).map(p => {
        const price = p.isPromo ? `USD ${p.promoPrice} 🔥` : `USD ${p.price}`;
        return `• ${p.name} — ${price}`;
      }).join('\n');
      return `I found these products that might match:\n\n${list}\n\nIs this what you were looking for? Feel free to ask me anything else!`;
    }
  }

  return `I'm not sure I understood that fully. Here's what I can help you with:\n\n• 📦 Product prices & availability\n• 🔧 Repair services\n• 📍 Store location & hours\n• 💳 Payment methods\n• 🚚 Delivery options\n• 🔥 Current promotions\n\nJust ask me something like "Do you have HP laptops?" or "How much is a Samsung phone?" and I'll find it for you! 😊`;
};

const WELCOME = {
  id: 'welcome',
  role: 'assistant',
  text: "👋 Hi! I'm TechBot, Techdave Solutions' assistant. I can tell you about our products, prices, services, repairs, delivery and more.\n\nWhat can I help you with today?",
};

const ChatAgent = () => {
  const { products } = useStore();
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input,    setInput]    = useState('');
  const [typing,   setTyping]   = useState(false);
  const bottomRef  = useRef();
  const inputRef   = useRef();

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, typing]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const send = (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput('');
    const userMsg = { id: Date.now() + '_u', role: 'user', text: msg };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);
    const delay = 600 + Math.random() * 600;
    setTimeout(() => {
      const reply = generateReply(msg, products);
      setMessages(prev => [...prev, { id: Date.now() + '_a', role: 'assistant', text: reply }]);
      setTyping(false);
    }, delay);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const reset = () => { setMessages([WELCOME]); setInput(''); };

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(o => !o)} style={S.floatBtn(open)} aria-label="TechBot">
        {open
          ? <span style={{ fontSize: '18px' }}>✕</span>
          : <>
              <img src={BOT_ICON} alt="TechBot" style={{ width: '28px', height: '28px' }} />
              <span style={S.floatLabel}>Ask TechBot</span>
              <span style={S.pulse} />
            </>
        }
      </button>

      {/* Chat window */}
      {open && (
        <div style={S.window}>
          {/* Header */}
          <div style={S.header}>
            <div style={S.headerLeft}>
              <div style={S.avatar}>
                <img src={BOT_ICON} alt="TechBot" style={{ width: '26px', height: '26px' }} />
              </div>
              <div>
                <div style={S.botName}>TechBot</div>
                <div style={S.botStatus}><span style={S.dot} /> Online · Techdave Solutions</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={reset} style={S.hBtn} title="New chat">🔄</button>
              <button onClick={() => setOpen(false)} style={S.hBtn}>✕</button>
            </div>
          </div>

          {/* Messages */}
          <div style={S.msgs}>
            {messages.map(m => (
              <div key={m.id} style={S.row(m.role)}>
                {m.role === 'assistant' && (
                  <div style={S.msgAvatar}>
                    <img src={BOT_ICON} alt="TechBot" style={{ width: '18px', height: '18px' }} />
                  </div>
                )}
                <div style={S.bubble(m.role)}>
                  {m.text.split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}{i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div style={S.row('assistant')}>
                <div style={S.msgAvatar}>
                  <img src={BOT_ICON} alt="TechBot" style={{ width: '18px', height: '18px' }} />
                </div>
                <div style={S.typingBubble}>
                  <span style={S.tdot(0)} /><span style={S.tdot(1)} /><span style={S.tdot(2)} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick questions */}
          {messages.length <= 1 && (
            <div style={S.quickArea}>
              <div style={S.quickLabel}>Quick questions:</div>
              <div style={S.quickGrid}>
                {QUICK_QUESTIONS.map(q => (
                  <button key={q} onClick={() => send(q)} style={S.qBtn}>{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div style={S.inputRow}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about products, prices, repairs…"
              style={S.textarea}
              rows={1}
              disabled={typing}
            />
            <button
              onClick={() => send()}
              style={S.sendBtn(!!input.trim() && !typing)}
              disabled={!input.trim() || typing}
            >
              ➤
            </button>
          </div>

          {/* Footer */}
          <div style={S.footer}>📍 B30 Eastgate Mall, Harare &nbsp;·&nbsp; Mon–Sat 8AM–6PM</div>
        </div>
      )}

      <style>{`
        @keyframes tdot { 0%,80%,100%{transform:translateY(0);opacity:.4} 40%{transform:translateY(-6px);opacity:1} }
        @keyframes pulse { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(1.9);opacity:0} }
      `}</style>
    </>
  );
};

const S = {
  floatBtn: (open) => ({
    position: 'fixed', bottom: '28px', left: '28px', zIndex: 1000,
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: open ? '14px' : '12px 20px 12px 14px',
    borderRadius: '100px', border: 'none',
    background: open ? 'rgba(80,80,80,0.9)' : 'linear-gradient(135deg,#CC0000,#990000)',
    color: '#fff', fontWeight: '700', cursor: 'pointer',
    boxShadow: '0 4px 24px rgba(204,0,0,0.4)', transition: 'all 0.3s',
  }),
  floatLabel: { fontSize: '14px', fontWeight: '700', whiteSpace: 'nowrap' },
  pulse: {
    position: 'absolute', width: '52px', height: '52px', borderRadius: '50%',
    border: '2px solid #CC0000', animation: 'pulse 2s ease-out infinite', pointerEvents: 'none',
  },
  window: {
    position: 'fixed', bottom: '92px', left: '28px', zIndex: 1000,
    width: '360px', maxHeight: '560px',
    backgroundColor: '#1a1a1a', borderRadius: '20px',
    border: '1px solid rgba(204,0,0,0.3)',
    boxShadow: '0 16px 60px rgba(0,0,0,0.6)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px',
    background: 'linear-gradient(135deg,#CC0000,#990000)',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  botName:   { fontSize: '15px', fontWeight: '800', color: '#fff' },
  botStatus: { fontSize: '11px', color: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', gap: '5px' },
  dot: {
    width: '7px', height: '7px', borderRadius: '50%',
    backgroundColor: '#4ade80', boxShadow: '0 0 6px #4ade80', display: 'inline-block',
  },
  hBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
    width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontSize: '13px',
  },
  msgs: {
    flex: 1, overflowY: 'auto', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  row: (role) => ({
    display: 'flex', justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
    alignItems: 'flex-end', gap: '8px',
  }),
  msgAvatar: {
    width: '28px', height: '28px', borderRadius: '50%',
    backgroundColor: 'rgba(204,0,0,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  bubble: (role) => ({
    maxWidth: '82%', padding: '10px 14px',
    borderRadius: role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    backgroundColor: role === 'user' ? '#CC0000' : '#2a2a2a',
    color: '#fff', fontSize: '13.5px', lineHeight: 1.6,
    border: role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.07)',
    whiteSpace: 'pre-wrap',
  }),
  typingBubble: {
    padding: '12px 16px', borderRadius: '18px 18px 18px 4px',
    backgroundColor: '#2a2a2a', border: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', gap: '5px', alignItems: 'center',
  },
  tdot: (i) => ({
    width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#CC0000',
    animation: `tdot 1.2s ease-in-out ${i * 0.2}s infinite`, display: 'inline-block',
  }),
  quickArea: { padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  quickLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' },
  quickGrid:  { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  qBtn: {
    padding: '6px 12px', borderRadius: '100px',
    border: '1px solid rgba(204,0,0,0.35)',
    backgroundColor: 'rgba(204,0,0,0.08)', color: '#ff9090',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  inputRow: {
    display: 'flex', gap: '8px', padding: '12px 14px',
    borderTop: '1px solid rgba(255,255,255,0.06)', alignItems: 'flex-end',
  },
  textarea: {
    flex: 1, backgroundColor: '#242424',
    border: '1.5px solid rgba(204,0,0,0.25)',
    borderRadius: '12px', padding: '10px 14px',
    color: '#fff', fontSize: '14px', fontFamily: 'inherit',
    outline: 'none', resize: 'none', lineHeight: 1.5, maxHeight: '100px',
  },
  sendBtn: (active) => ({
    width: '40px', height: '40px', borderRadius: '12px', border: 'none',
    background: active ? 'linear-gradient(135deg,#CC0000,#990000)' : 'rgba(255,255,255,0.08)',
    color: active ? '#fff' : 'rgba(255,255,255,0.3)',
    fontSize: '16px', cursor: active ? 'pointer' : 'not-allowed',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    transition: 'all 0.2s',
  }),
  footer: {
    padding: '8px 16px 12px', fontSize: '11px',
    color: 'rgba(255,255,255,0.3)', textAlign: 'center',
  },
};

export default ChatAgent;